import type { ReactNode } from 'react';

import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { AnalyticsEmptyState } from '../aiAgents/AnalyticsEmptyState';
import {
  METRIC_TOOLTIP_CARD_CLASSES,
  MetricTooltipRow,
  MetricTooltipSectionLabel,
} from '../tooltip/MetricTooltipCard';

export type DistributionBarItem = {
  id: string;
  label: string;
  /**
   * A 0–100 percentage. Values outside the range are clamped. Pass a true
   * percentage (e.g. `0.5` for half a percent, `50` for fifty) — do NOT pass a
   * 0–1 ratio, as the two scales are indistinguishable for sub-1% shares.
   */
  value: number;
  /**
   * Optional previous-period value (same 0–100 scale as `value`). When provided
   * on any item, the card switches to the compare-mode stacked bar rendering —
   * saturated segment up to `min(value, previousValue)`, lighter cap up to
   * `max(value, previousValue)`.
   */
  previousValue?: number;
  /** Tailwind class for the bar fill (e.g. "bg-chart-1"). */
  barClassName: string;
  /**
   * Optional label shown in the tooltip header. Falls back to `label` when
   * omitted, allowing a concise bar label to pair with a fuller tooltip title
   * (e.g. bar "Partially" / tooltip "Partially achieved").
   */
  tooltipLabel?: string;
  /**
   * Optional second row rendered below the percentage row in the tooltip.
   * Used by categorical insight bars to show an absolute count alongside
   * the percentage (e.g. "Users 230").
   */
  extraRow?: { label: string; value: string };
};

type ExportConfig = {
  onExport: (format: ExportFormat) => void;
  isExporting: boolean;
  disabled?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// `value` is already a 0–100 percentage; just guard NaN/Infinity and clamp.
// (We deliberately do not rescale a 0–1 ratio here: a genuine sub-1% share like
// 0.5% is indistinguishable from the ratio 0.5, so guessing inflated small
// shares 100×.)
const toPercent = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return clamp(value, 0, 100);
};

// Render a genuine but sub-1% share as "<1%" rather than a rounded "0%", so a
// non-zero slice (e.g. 2 of 610 users) never looks like it has no data. Used
// for the compact bar label.
const formatPercent = (value: number) => {
  if (value > 0 && value < 1) {
    return '<1%';
  }
  return `${Math.round(value)}%`;
};

// Precise percentage for the hover tooltip — one decimal, e.g. "84.1%". Whole
// numbers drop the trailing ".0" ("50%"), and a tiny but non-zero share shows
// "<0.1%" so it never collapses to "0%".
export const formatPercentExact = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0%';
  }
  const rounded = Math.round(value * 10) / 10;
  if (rounded < 0.1) {
    return '<0.1%';
  }
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

const DEFAULT_BARS_ROW = 'flex w-full items-end justify-between';
const DEFAULT_BAR_COLUMN = 'flex w-20 shrink-0 flex-col items-center';
const DEFAULT_BAR_BUTTON_WIDTH = 'w-20';
const DEFAULT_BAR_LABEL = 'w-20 text-center text-xs font-normal text-foreground-muted';
const DEFAULT_CHART_LABELS_GAP = 'gap-2';
const DEFAULT_BAR_RADIUS = 'rounded-2xl';
const GRID_LINE_KEYS = ['top', 'q1', 'mid', 'q3', 'baseline'] as const;
const MIN_BAR_HEIGHT_PX = 12;

export const DistributionBarCard = ({
  title,
  titleTooltip,
  description,
  items,
  exportConfig,
  valueLabel = 'Percentage',
  className,
  headerRight,
  /** Scales bar height; default 300 so a 55% value renders ~165px per Figma. */
  maxBarHeightPx = 300,
  barSizing = 'fixed',
  barsRowClassName = DEFAULT_BARS_ROW,
  barColumnClassName = DEFAULT_BAR_COLUMN,
  barButtonClassName = DEFAULT_BAR_BUTTON_WIDTH,
  barLabelClassName = DEFAULT_BAR_LABEL,
  /** Vertical gap between the chart (bars + grid lines) and the labels row. */
  chartLabelsGapClassName = DEFAULT_CHART_LABELS_GAP,
  /** Tailwind radius class applied to the bar button and fill. */
  barRadiusClassName = DEFAULT_BAR_RADIUS,
  /** Whether to render faint horizontal grid lines behind the bars. */
  showGridLines = true,
  /** Render the percentage value as a small label above each bar. */
  showValueAboveBar = false,
  /** Optional node rendered below the bar labels (e.g. compare-mode delta). */
  footer,
  emptyState,
  isEmpty: isEmptyOverride,
}: {
  title: string;
  titleTooltip?: string;
  description?: string;
  items: DistributionBarItem[];
  exportConfig?: ExportConfig;
  /** Tooltip label for the numeric value (e.g. "Percentage", "Share"). */
  valueLabel?: string;
  className?: string;
  /** Optional custom header action; overrides `exportConfig` when provided. */
  headerRight?: ReactNode;
  maxBarHeightPx?: number;
  barSizing?: 'fixed' | 'fill';
  barsRowClassName?: string;
  barColumnClassName?: string;
  barButtonClassName?: string;
  barLabelClassName?: string;
  chartLabelsGapClassName?: string;
  barRadiusClassName?: string;
  showGridLines?: boolean;
  showValueAboveBar?: boolean;
  footer?: ReactNode;
  /** Title/description shown instead of the bars when the empty state is active. */
  emptyState?: { title: string; description: string };
  /**
   * When provided, overrides the default empty detection. Pass `true` from the
   * caller when the data layer knows the response is missing/null. Defaults to
   * "no items" — an all-zero distribution is treated as valid data.
   */
  isEmpty?: boolean;
}) => {
  const { t } = useTranslation();
  const previousPeriodLabel = t('analytics.overview_new.previous_period');
  const isEmpty = isEmptyOverride ?? items.length === 0;
  const isFill = barSizing === 'fill';

  return (
    <Card
      className={cn(
        'flex h-full min-h-0 flex-col rounded-lg bg-background text-foreground shadow-none',
        className
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <CardTitle className="flex items-center gap-1.5">
              <span className="truncate">{title}</span>
              {titleTooltip && (
                <InfoTooltip content={titleTooltip} size="large">
                  <InfoIcon className="size-4 shrink-0 text-foreground-muted" />
                </InfoTooltip>
              )}
            </CardTitle>
            {description && <CardDescription className="truncate">{description}</CardDescription>}
          </div>
          {headerRight ??
            (exportConfig && !isEmpty ? (
              <ExportButton
                variant="icon"
                onExport={exportConfig.onExport}
                isExporting={exportConfig.isExporting}
                disabled={exportConfig.disabled}
              />
            ) : null)}
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        {isEmpty && emptyState ? (
          <div className="flex flex-1 items-center justify-center">
            <AnalyticsEmptyState title={emptyState.title} description={emptyState.description} />
          </div>
        ) : (
          <div className={cn('flex flex-1 flex-col', chartLabelsGapClassName)}>
            <div className="relative flex flex-1 items-end">
              {showGridLines && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex flex-col justify-between"
                >
                  {GRID_LINE_KEYS.map((key) => (
                    <div key={key} className="h-px w-full bg-border" />
                  ))}
                </div>
              )}

              <div className={cn('relative', isFill && 'h-full', barsRowClassName)}>
                {items.map((item) => {
                  const percent = toPercent(item.value);
                  const hasPrevious = item.previousValue != null;
                  const previousPercent = hasPrevious ? toPercent(item.previousValue!) : percent;
                  const topPercent = Math.max(percent, previousPercent);
                  const overlapPercent = Math.min(percent, previousPercent);

                  if (topPercent <= 0) {
                    return (
                      <div
                        key={item.id}
                        className={cn(barColumnClassName, isFill && 'relative h-full')}
                      >
                        {showValueAboveBar && (
                          <span
                            className={cn(
                              'text-xs leading-4 font-medium text-foreground',
                              isFill ? 'absolute bottom-0 left-1/2 -translate-x-1/2' : 'mb-1'
                            )}
                          >
                            {formatPercent(percent)}
                          </span>
                        )}
                      </div>
                    );
                  }

                  const totalHeightPx = clamp(
                    (topPercent / 100) * maxBarHeightPx,
                    MIN_BAR_HEIGHT_PX,
                    maxBarHeightPx
                  );
                  const overlapHeightPx = hasPrevious
                    ? clamp((overlapPercent / 100) * maxBarHeightPx, 0, totalHeightPx)
                    : totalHeightPx;
                  const capHeightPx = Math.max(totalHeightPx - overlapHeightPx, 0);

                  const overlapFraction = hasPrevious ? (overlapPercent / topPercent) * 100 : 100;
                  const capFraction = Math.max(100 - overlapFraction, 0);
                  const showCap = hasPrevious && (isFill ? capFraction > 0 : capHeightPx > 0);

                  const valueBadge = showValueAboveBar && (
                    <span
                      className={cn(
                        'text-xs leading-4 font-medium text-foreground',
                        isFill ? 'absolute bottom-full left-1/2 mb-1 -translate-x-1/2' : 'mb-1'
                      )}
                    >
                      {formatPercent(percent)}
                    </span>
                  );

                  const barButton = (
                    <button
                      type="button"
                      className={cn(
                        'flex flex-col-reverse overflow-hidden transition-opacity outline-none',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        'hover:opacity-90',
                        barRadiusClassName,
                        barButtonClassName
                      )}
                      style={isFill ? { height: '100%' } : { height: totalHeightPx }}
                      aria-label={
                        hasPrevious
                          ? `${item.label}: ${formatPercent(percent)} (previous: ${formatPercent(previousPercent)})`
                          : `${item.label}: ${formatPercent(percent)}`
                      }
                    >
                      <div
                        className={cn('w-full', item.barClassName)}
                        style={
                          isFill ? { height: `${overlapFraction}%` } : { height: overlapHeightPx }
                        }
                      />
                      {showCap && (
                        <div
                          className={cn('w-full opacity-40', item.barClassName)}
                          style={isFill ? { height: `${capFraction}%` } : { height: capHeightPx }}
                        />
                      )}
                    </button>
                  );

                  const barWithTooltip = (
                    <InfoTooltip
                      content={
                        <>
                          <p className="mb-3 text-sm leading-none font-medium">
                            {item.tooltipLabel ?? item.label}
                          </p>
                          <div className="space-y-1 text-xs leading-4 font-normal">
                            <MetricTooltipRow
                              label={valueLabel}
                              value={formatPercentExact(percent)}
                            />
                            {item.extraRow && (
                              <MetricTooltipRow
                                label={item.extraRow.label}
                                value={item.extraRow.value}
                              />
                            )}
                            {hasPrevious && (
                              <>
                                <MetricTooltipSectionLabel>
                                  {previousPeriodLabel}
                                </MetricTooltipSectionLabel>
                                <MetricTooltipRow
                                  label={valueLabel}
                                  value={formatPercentExact(previousPercent)}
                                />
                              </>
                            )}
                          </div>
                        </>
                      }
                      variant="default"
                      side="top"
                      align="center"
                      size="auto"
                      showArrow={false}
                      className={cn(METRIC_TOOLTIP_CARD_CLASSES, 'min-w-60')}
                    >
                      {barButton}
                    </InfoTooltip>
                  );

                  if (isFill) {
                    return (
                      <div key={item.id} className={cn(barColumnClassName, 'relative h-full')}>
                        <div
                          className="absolute inset-x-0 bottom-0 flex flex-col items-center"
                          style={{ height: `${topPercent}%`, minHeight: MIN_BAR_HEIGHT_PX }}
                        >
                          {valueBadge}
                          {barWithTooltip}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.id} className={barColumnClassName}>
                      {valueBadge}
                      {barWithTooltip}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={cn(barsRowClassName, 'items-start')}>
              {items.map((item) => (
                <div key={item.id} className={barColumnClassName}>
                  <div className={barLabelClassName}>{item.label}</div>
                </div>
              ))}
            </div>
            {footer && <div className="flex justify-center">{footer}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
